import { z } from "zod";
import { ENTITY_TYPES, GENRE_SOURCES } from "../constants.ts";
import { EntityIdSchema, HexColorSchema, IsoDateTime, LocalIdSchema } from "./common.ts";
import { FieldValueDataSchema } from "./fieldValue.ts";
import { EntityStatusSchema } from "./status.ts";

/**
 * The detail sheet renders one shape for every entity, so the panel does not carry
 * a branch per type. Genre is a first-class entity (fields attach when configured).
 */
export const DETAIL_TYPES = [...ENTITY_TYPES] as const;

export const DetailTypeSchema = z.enum(DETAIL_TYPES);

export type DetailType = z.infer<typeof DetailTypeSchema>;

/** Whether a detail type also exists as a field/status/genre-carrying entity. */
export function isEntityType(type: DetailType): boolean {
  return (ENTITY_TYPES as readonly string[]).includes(type);
}

/** How the client formats a fact. The server never sends display strings. */
export const FACT_KINDS = [
  "text",
  "number",
  "date",
  "datetime",
  "duration",
  "url",
  "bool",
  "ratio",
] as const;

export const DetailLinkSchema = z.object({
  type: DetailTypeSchema,
  id: EntityIdSchema,
});

export type DetailLink = z.infer<typeof DetailLinkSchema>;

export const DetailFactSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  kind: z.enum(FACT_KINDS),
  /** Set when the fact points at another entity the sheet can open. */
  link: DetailLinkSchema.nullable(),
});

export type DetailFact = z.infer<typeof DetailFactSchema>;

/**
 * Provider-derived numbers are kept apart from mirror facts and carry their
 * source, because "tempo 128" means something different coming from ReccoBeats
 * than from Spotify — and neither is the user's own work.
 */
export const DetailProviderBlockSchema = z.object({
  label: z.string(),
  source: z.string(),
  fetched_at: IsoDateTime.nullable(),
  facts: z.array(DetailFactSchema),
});

export type DetailProviderBlock = z.infer<typeof DetailProviderBlockSchema>;

/** An assignment plus the genre it points at, so chips need no second request. */
export const DetailGenreSchema = z.object({
  genre_id: LocalIdSchema,
  name: z.string(),
  slug: z.string(),
  color: HexColorSchema.nullable(),
  source: z.enum(GENRE_SOURCES),
  confidence: z.number().min(0).max(1).nullable(),
});

export type DetailGenre = z.infer<typeof DetailGenreSchema>;

/**
 * Suggested genre chip (not yet assigned). Multi-source: AcousticBrainz,
 * MusicBrainz tags, file metadata. `source_label` is the hover hint.
 */
export const DetailSuggestedGenreSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  /** MB vote count; 0 when confidence-based (AB). */
  count: z.number().int().nonnegative(),
  /** AB classifier probability 0..1; null for vote-based tags. */
  confidence: z.number().min(0).max(1).nullable(),
  source: z.enum([
    "acousticbrainz",
    "musicbrainz",
    "musicbrainz_artist",
    "file_tag",
  ]),
  /** e.g. "AcousticBrainz · 83%" or "MusicBrainz · 3 votes" */
  source_label: z.string().min(1),
  genre_id: LocalIdSchema.nullable(),
});

export type DetailSuggestedGenre = z.infer<typeof DetailSuggestedGenreSchema>;

export const EntityDetailSchema = z.object({
  entity_type: DetailTypeSchema,
  entity_id: EntityIdSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  /**
   * When set, the sheet header subtitle renders these as clickable links
   * (e.g. track artists) instead of plain text.
   */
  subtitle_links: z
    .array(
      z.object({
        type: DetailTypeSchema,
        id: EntityIdSchema,
        label: z.string(),
      }),
    )
    .nullable(),
  image_url: z.string().nullable(),
  external_url: z.string().nullable(),
  facts: z.array(DetailFactSchema),
  /** Which editable sections apply. Drives which tabs exist at all. */
  supports: z.object({
    fields: z.boolean(),
    status: z.boolean(),
    genres: z.boolean(),
  }),
  status: EntityStatusSchema.nullable(),
  /**
   * User assignments (most-specific only) — or playlist frequency chips when
   * `genres_computed` is true.
   */
  genres: z.array(DetailGenreSchema),
  /** MB suggestions after hers; empty when unsupported or none. */
  suggested_genres: z.array(DetailSuggestedGenreSchema).default([]),
  /** Playlist aggregate chips — not editable assignments. */
  genres_computed: z.boolean().default(false),
  /** Custom field values keyed by field `key`, matching `PATCH /api/values`. */
  values: z.record(z.string(), FieldValueDataSchema),
  providers: z.array(DetailProviderBlockSchema),
  /**
   * MusicBrainz sample / sampling relations on a track recording.
   * Empty when none or not a track.
   */
  sample_relations: z
    .array(
      z.object({
        type: z.string(),
        name: z.string(),
        url: z.string().url().nullable(),
      }),
    )
    .default([]),
  /**
   * Wikipedia page when present on the linked MusicBrainz artist (url-rel).
   * Best single drift-preventer for research prompts.
   */
  wikipedia_url: z.string().url().nullable().default(null),
  /**
   * MusicBrainz ISO-2 suggestion for artists when the user Country field is empty.
   * Null on non-artists or when MB has no country.
   */
  suggested_country: z.string().nullable().default(null),
  /** Display BPM for tracks (user override, else enrichment). */
  bpm: z.number().nullable().default(null),
  /** True when `bpm` is this user's manual value. */
  bpm_manual: z.boolean().default(false),
});

export type EntityDetail = z.infer<typeof EntityDetailSchema>;

export const EntityDetailResponseSchema = z.object({ detail: EntityDetailSchema });
